import { ReportTimeWindow } from "@app/pages/Dashboard";
import { createContext, PropsWithChildren, useContext, useState } from "react";

export interface Filter {
  id: string;
  name: string;
}

interface DashboardContextType {
  timeWindow: ReportTimeWindow;
  setTimeWindow: React.Dispatch<React.SetStateAction<ReportTimeWindow>>;
  customDateRange?: { startDate: Date; endDate: Date };
  setCustomDateRange?: React.Dispatch<
    React.SetStateAction<{ startDate: Date; endDate: Date } | undefined>
  >;
  dataIsEmpty?: boolean;
  setDataIsEmpty?: React.Dispatch<React.SetStateAction<boolean>>;
  groupFilter?: Filter[];
  setGroupFilter?: React.Dispatch<React.SetStateAction<Filter[]>>;
  storyFilter?: Filter[];
  setStoryFilter?: React.Dispatch<React.SetStateAction<Filter[]>>;
}

export interface DashboardProviderProps extends PropsWithChildren {
  initialTimeWindow: ReportTimeWindow;
  initialCustomDateRange?: { startDate: Date; endDate: Date };
  initialDataIsEmpty?: boolean;
}

const DashboardContext = createContext<DashboardContextType | undefined>(
  undefined,
);

export const DashboardProvider = ({
  children,
  ...props
}: DashboardProviderProps) => {
  const [timeWindow, setTimeWindow] = useState<ReportTimeWindow>(
    props.initialTimeWindow,
  );
  const [customDateRange, setCustomDateRange] = useState<
    { startDate: Date; endDate: Date } | undefined
  >(props.initialCustomDateRange);
  const [dataIsEmpty, setDataIsEmpty] = useState<boolean>(
    props.initialDataIsEmpty || false,
  );

  const [groupFilter, setGroupFilter] = useState<Filter[]>([]);
  return (
    <DashboardContext.Provider
      value={{
        timeWindow,
        setTimeWindow,
        customDateRange,
        setCustomDateRange,
        dataIsEmpty,
        setDataIsEmpty,
        groupFilter,
        setGroupFilter,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error(
      "useDashboardContext must be used within a DashboardProvider",
    );
  }

  return context;
};
